import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  PlusIcon,
  CalendarIcon,
  CheckCircleIcon,
  UserIcon,
  EyeIcon,
  InformationCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { updateUrlWithException, dispatchWithToast } from "../../../../components/Utils";
import { debounce } from "lodash";
import { getUsersAsync, createUserAsync } from "../../../../slices/user";
import { UsersTable } from "../../../../components/dashboard/company/users/users-table";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { checkEmail, resendInvitation } from "../../../../api/user";
import { toast } from "react-hot-toast";

export const CompanyUsersList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const usersState = useSelector((store) => {
    return store.users;
  });

  const { isLoading } = usersState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchParams] = useSearchParams();
  const [emailSearchValue, setEmailSearchValue] = useState("");
  const [emailCheckResult, setEmailCheckResult] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [resendingInvitation, setResendingInvitation] = useState(false);

  const [controller, setController] = useState({
    filters: [],
    per_page: 25,
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy") ? searchParams.get("sortBy") : "employee_number",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    showInDropdown: searchParams.get("showInDropdown") === "true",
    active: searchParams.get("active") !== null ? searchParams.get("active") === "true" : true, // Default to true (only active users)
  });

  // Update all params from controller except filters (dont't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        showInDropdown: controller.showInDropdown,
        active: controller.active,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleFiltersChange = (filters) => {
    // Convert ModuleFilter format to backend format
    const backendFilters = [];

    // Handle active filter - send as query parameter, not as filter
    // filters.active will be handled separately below

    if (filters.employee) {
      backendFilters.push({
        name: "employee",
        value: true,
      });
    }

    if (filters.showInDropdown) {
      backendFilters.push({
        name: "showInDropdown",
        value: true,
      });
    }

    // Handle driver filter (if still using status)
    if (filters.statuses && filters.statuses.includes("driver")) {
      backendFilters.push({
        name: "driver",
        value: true,
      });
    }

    // Handle role filters (if still using status)
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach((status) => {
        if (status !== "employee" && status !== "driver") {
          backendFilters.push({
            name: "role",
            value: status,
          });
        }
      });
    }

    // When searching, remove active filter to show all matching results
    // This provides better UX - users expect to find what they search for
    // Note: Other filters (like showInDropdown) are kept as they may be context-specific
    const hasSearchQuery = filters.query !== undefined && filters.query.trim() !== "";
    const activeFilter = hasSearchQuery 
      ? undefined // Remove active filter when searching
      : (filters.active !== undefined ? filters.active : controller.active);

    setController({
      ...controller,
      page: 1,
      query: filters.query !== undefined ? filters.query : controller.query,
      filters: backendFilters,
      active: activeFilter, // Update active parameter (removed when searching)
    });
  };

  const [userDialogOpen, setUserDialogOpen] = useState(false);

  // Define custom checkbox button filters for users
  const customCheckboxButtonFilters = [
    {
      key: "active",
      label: t("company.users.filter.active"),
      icon: CheckCircleIcon,
      defaultValue: true,
    },
    {
      key: "employee",
      label: t("company.users.filter.employee"),
      icon: UserIcon,
      defaultValue: true,
    },
    {
      key: "showInDropdown",
      label: t("company.users.filter.show_in_dropdown"),
      icon: EyeIcon,
      defaultValue: false,
    },
  ];

  const debouncedDispatchAction = useCallback(
    debounce((controller) => {
      dispatch(getUsersAsync(controller));
    }, 250),
    [dispatch]
  );

  const debouncedCheckEmail = useCallback(
    debounce(async (email) => {
      if (!email || email.trim() === "") {
        setEmailCheckResult(null);
        return;
      }

      setCheckingEmail(true);
      try {
        const result = await checkEmail(email);
        setEmailCheckResult(result);
      } catch (error) {
        console.error("Error checking email:", error);
        setEmailCheckResult(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 500),
    []
  );

  const handleEmailSearchChange = (value, formik) => {
    setEmailSearchValue(value);
    formik.setFieldValue("email", value);
    debouncedCheckEmail(value);
  };

  const handleResendInvitation = async () => {
    const userId = emailCheckResult?.user?.id;
    const normalizedEmail = emailSearchValue?.trim();

    if (!userId || !normalizedEmail) return;

    setResendingInvitation(true);
    try {
      const response = await resendInvitation(userId);
      if (response?.errors?.length) {
        toast.error(response.errors.join(", "));
        return;
      }

      toast.success(response?.message || t("company.users.invitation.resend_success"));

      // Refresh email check state so the UI reflects updated invitation status.
      const refreshedResult = await checkEmail(normalizedEmail);
      setEmailCheckResult(refreshedResult);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errors?.join(", ") ||
        t("company.users.invitation.resend_error");
      toast.error(errorMessage);
    } finally {
      setResendingInvitation(false);
    }
  };

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, debouncedDispatchAction]);

  useEffect(() => {
    document.title = `${t("company.users.page_title")} | Promang`;
  }, [t]);

  const openUsersCalendar = () => {
    const today = new Date().toISOString().split("T")[0];
    if (selectedItems.length > 0) {
      const userIds = selectedItems.map((user) => user.id).join(",");
      window.open(
        `/dashboard/calendar?date=${today}&view=day&users=${userIds}&showWeekNumbers=false`,
        "_blank"
      );
    } else {
      window.open(
        `/dashboard/calendar?date=${today}&view=day&showWeekNumbers=false`,
        "_blank"
      );
    }
  };

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
      statuses: [],
      employee: false,
      active: controller.active !== undefined ? controller.active : true, // Read from controller.active, default to true
      showInDropdown: false,
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        switch (filter.name) {
          case "employee":
            if (filter.value === true) {
              initialFilters.employee = true;
            }
            break;
          case "driver":
            if (filter.value === true) {
              initialFilters.statuses.push("driver");
            }
            break;
          case "showInDropdown":
            if (filter.value === true) {
              initialFilters.showInDropdown = true;
            }
            break;
          case "role":
            initialFilters.statuses.push(filter.value);
            break;
        }
      });
    }

    return initialFilters;
  };

  const totalUsers = usersState.totalCount ?? 0;
  const onPageCount = usersState.currentCount ?? usersState.data?.length ?? 0;

  return (
    <>
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  <UserGroupIcon className="h-4 w-4" />
                  {t("company.users.hero.kicker")}
                </div>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                  {t("company.users.page_title")}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  {t("company.users.hero.subtitle")}
                </p>
              </div>

              <div className="flex w-full flex-col gap-4 lg:max-w-xl">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl bg-slate-950 px-4 py-3 text-white">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-300">
                      {t("company.users.stats.total")}
                    </div>
                    <div className="mt-2 text-2xl font-semibold tabular-nums">
                      {totalUsers}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-100 px-4 py-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      {t("company.users.stats.on_page")}
                    </div>
                    <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
                      {onPageCount}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-slate-100 px-4 py-3 sm:col-span-1 col-span-2">
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      {t("company.users.stats.selected")}
                    </div>
                    <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-900">
                      {selectedItems.length}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={openUsersCalendar}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    <CalendarIcon className="h-4 w-4 text-slate-600" />
                    {t("company.users.calendar")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserDialogOpen(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                  >
                    <PlusIcon className="h-4 w-4" />
                    {t("company.users.new_user")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <section className="mb-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-5">
              <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                {t("company.users.filters_heading")}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {t("company.users.filters_description")}
              </p>
            </div>
            <div className="space-y-5 px-6 py-6">
              <ModuleFilter
                className="mt-0"
                disabled={isLoading}
                onFiltersChange={handleFiltersChange}
                initialFilters={getInitialFilters()}
                searchPlaceholder={t("company.users.search_placeholder")}
                filterTitle={t("company.users.filter_title")}
                customCheckboxButtonFilters={customCheckboxButtonFilters}
                acceptedStatuses={["driver"]}
              />
            </div>
          </section>

          <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                <UserGroupIcon className="h-4 w-4" />
                {t("company.users.section.kicker")}
              </div>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                {t("company.users.section.title")}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {t("company.users.section.subtitle")}
              </p>
            </div>

            <UsersTable
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={usersState}
              itemsCount={totalUsers}
              currentCount={onPageCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
            />
          </section>
        </div>
      </div>

      <PMDialog
        open={userDialogOpen}
        onClose={() => {
          setUserDialogOpen(false);
          setEmailSearchValue("");
          setEmailCheckResult(null);
        }}
        title={t("Create New User")}
        size="2xl"
      >
        <Formik
          initialValues={{
            name: "",
            email: "",
            mobile: "",
            mobile_work: "",
            active: true,
            approved: true,
            show_in_dropdown: false,
          }}
          validationSchema={Yup.object({
            name: Yup.string()
              .max(255)
              .when("$emailExists", {
                is: false,
                then: (schema) => schema.required(t("Name is required")),
                otherwise: (schema) => schema,
              }),
            email: Yup.string()
              .email(t("Invalid email address"))
              .required(t("Email is required")),
            mobile: Yup.string(),
            mobile_work: Yup.string(),
          })}
          validationContext={{ emailExists: emailCheckResult?.exists }}
          onSubmit={(values, helpers) => {
            try {
              dispatchWithToast(
                dispatch(createUserAsync(values)),
                (data) => {
                  // Refresh user list after successful creation
                  dispatch(getUsersAsync(controller));
                  setUserDialogOpen(false);
                  helpers.resetForm();
                  setEmailSearchValue("");
                  setEmailCheckResult(null);
                },
                () => {
                  setUserDialogOpen(false);
                }
              );
              helpers.setSubmitting(false);
            } catch (err) {
              console.error(err);
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: err.message });
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => {
            const emailExists = emailCheckResult?.exists;
            const alreadyAssociated = emailCheckResult?.already_associated;
            const hasPendingInvitation = emailCheckResult?.has_pending_invitation;
            const canResendInvitation = emailCheckResult?.can_resend_invitation;
            const existingUser = emailCheckResult?.user;

            return (
              <Form>
                <div className="space-y-6">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("Search Email")}
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={emailSearchValue}
                        onChange={(e) => handleEmailSearchChange(e.target.value, formik)}
                        placeholder={t("Enter email to search...")}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                      />
                      {checkingEmail && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                    </div>
                    {emailCheckResult && emailExists && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-start">
                          <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-900">
                              {t("User already exists")}
                            </p>
                            {existingUser && (
                              <p className="text-sm text-blue-700 mt-1">
                                {t("Name")}: {existingUser.name}
                              </p>
                            )}
                            {alreadyAssociated ? (
                              <p className="text-sm text-blue-700 mt-1">
                                {t("User is already associated with this company")}
                              </p>
                            ) : hasPendingInvitation ? (
                              <p className="text-sm text-blue-700 mt-1">
                                {t("An invitation has already been sent to this user")}
                              </p>
                            ) : canResendInvitation ? (
                              <p className="text-sm text-blue-700 mt-1">
                                {t("company.users.invitation.expired")}
                              </p>
                            ) : (
                              <p className="text-sm text-blue-700 mt-1">
                                {t("An invitation will be sent to this user")}
                              </p>
                            )}
                            {canResendInvitation && !alreadyAssociated && (
                              <button
                                type="button"
                                onClick={handleResendInvitation}
                                disabled={resendingInvitation}
                                className="mt-3 inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {resendingInvitation
                                  ? t("company.users.invitation.resending")
                                  : t("company.users.invitation.resend_button")}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {!emailExists && (
                    <>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <PMField
                            name="name"
                            type="text"
                            label={t("Name")}
                            required
                            {...formik}
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <PMField
                            name="email"
                            type="email"
                            label={t("Email")}
                            required
                            disabled={emailSearchValue !== ""}
                            {...formik}
                          />
                        </div>

                        <div>
                          <PMField
                            name="mobile"
                            type="text"
                            label={t("Mobile")}
                            {...formik}
                          />
                        </div>

                        <div>
                          <PMField
                            name="mobile_work"
                            type="text"
                            label={t("Work Mobile")}
                            {...formik}
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <div className="grid grid-cols-2 gap-4">
                            <PMSwitch
                              label={t("Active")}
                              name="active"
                              {...formik}
                            />
                            <PMSwitch
                              label={t("Show in dropdown")}
                              name="show_in_dropdown"
                              {...formik}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setUserDialogOpen(false);
                      formik.resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formik.isSubmitting ? t("Creating...") : t("Create User")}
                  </button>
                </div>
              </div>
            </Form>
            );
          }}
        </Formik>
      </PMDialog>
    </>
  );
};
